import React, { useState, useEffect, useRef } from 'react';

export const useCountUp = (end, duration = 2000, suffix = '') => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const numEnd = parseInt(String(end).replace(/[^\d]/g, '')) || 0;
    if (numEnd === 0) { setCount(0); return; }
    const steps = 60;
    const stepTime = duration / steps;
    let current = 0;
    const increment = numEnd / steps;
    const timer = setInterval(() => {
      current += increment;
      if (current >= numEnd) { setCount(numEnd); clearInterval(timer); }
      else setCount(Math.floor(current));
    }, stepTime);
    return () => clearInterval(timer);
  }, [started, end, duration]);

  return { ref, count, suffix };
};

export const AnimatedStatSpecial = ({ num, label, electro = false }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  if (electro) return (
    <div ref={ref} className="text-center p-6 rounded-2xl bg-[#0a0f1c] border border-yellow-400/40 shadow-[0_0_20px_-4px_rgba(250,204,21,0.35)] hover:border-yellow-400/80 hover:shadow-[0_0_30px_-4px_rgba(250,204,21,0.6)] transition-all duration-300">
      <p className={`text-3xl font-black mb-1 bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent transition-all duration-700 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} style={{ textShadow: 'none' }}>{num}</p>
      <p className="text-sm text-slate-400">{label}</p>
    </div>
  );
  return (
    <div ref={ref} className="text-center p-6 rounded-xl bg-white shadow-sm">
      <p className={`text-3xl font-bold text-primary mb-1 transition-all duration-700 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>{num}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
};

export const AnimatedStatNumber = ({ num, label, electro = false }) => {
  const numStr = String(num);
  const numericPart = numStr.replace(/[^\d]/g, '');
  const prefix = numStr.match(/^[^\d]*/)?.[0] || '';
  const suffixMatch = numStr.match(/[^\d]+$/)?.[0] || '';
  const { ref, count } = useCountUp(numericPart, 2000);
  if (electro) return (
    <div ref={ref} className="text-center p-6 rounded-2xl bg-[#0a0f1c] border border-yellow-400/40 shadow-[0_0_20px_-4px_rgba(250,204,21,0.35)] hover:border-yellow-400/80 hover:shadow-[0_0_30px_-4px_rgba(250,204,21,0.6)] transition-all duration-300">
      <p className="text-3xl font-black mb-1 bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
        {prefix}{count.toLocaleString()}{suffixMatch}
      </p>
      <p className="text-sm text-slate-400">{label}</p>
    </div>
  );
  return (
    <div ref={ref} className="text-center p-6 rounded-xl bg-white shadow-sm">
      <p className="text-3xl font-bold text-primary mb-1">
        {prefix}{count.toLocaleString()}{suffixMatch}
      </p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
};

export const AnimatedStat = ({ num, label, electro = false }) => {
  if (String(num).includes('/')) return <AnimatedStatSpecial num={num} label={label} electro={electro} />;
  return <AnimatedStatNumber num={num} label={label} electro={electro} />;
};
